import { createRegistry } from './createRegistry';
import {
  PRECISION_STANDARD,
  PRECISION_CURRENCY,
  PRECISION_PERCENTAGE_EXTENDED,
} from '../precision';
import { maskSsn, maskPhone, maskTaxId, wholeNumberWithCommas } from '../primitives/formatters';

/**
 * Options passed to precision-aware format functions.
 * The engine resolves these from the field's JSON metadata
 * (`displayPrecision`, `displayCurrency`, `displayLocale`).
 */
export interface FormatOptions {
  precision?: number;
  currency?: string;
  locale?: string;
}

/**
 * A display-format function: takes a raw value + optional formatting options,
 * returns a human-readable display string.
 *
 * Distinct from `FormatterFn` (primitives/types.ts) which is `string → string`
 * for input masking. FormatFn handles display-time formatting with precision.
 */
export type FormatFn = (value: unknown, opts?: FormatOptions) => string;

export const FormatRegistry = createRegistry<FormatFn>('FormatRegistry');

// ---------------------------------------------------------------------------
// Numeric / monetary formatters (precision-aware)
// ---------------------------------------------------------------------------

FormatRegistry.register('CURRENCY', (value, opts) => {
  const precision = opts?.precision ?? PRECISION_CURRENCY;
  const currency = opts?.currency ?? 'USD';
  const locale = opts?.locale ?? 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(Number(value));
});

FormatRegistry.register('NUMBER', (value, opts) => {
  const precision = opts?.precision ?? PRECISION_STANDARD;
  const locale = opts?.locale ?? 'en-US';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(Number(value));
});

FormatRegistry.register('PERCENT', (value, opts) => {
  const precision = opts?.precision ?? PRECISION_PERCENTAGE_EXTENDED;
  return `${Number(value).toFixed(precision)}%`;
});

FormatRegistry.register('COMMA_WHOLE_NUMBER', (value) => {
  return wholeNumberWithCommas(String(value));
});

// ---------------------------------------------------------------------------
// Identity / masking display formatters (precision-independent)
// ---------------------------------------------------------------------------

FormatRegistry.register('SSN_US', (value) => maskSsn(String(value)));
FormatRegistry.register('PHONE_US', (value) => maskPhone(String(value)));
FormatRegistry.register('TAX_ID_US', (value) => maskTaxId(String(value)));

FormatRegistry.register('DATE_MDY', (value) => {
  const s = String(value);
  const parts = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
});

FormatRegistry.register('IDENTITY', (value) => String(value ?? ''));
